import {
  Autocomplete,
  Box,
  Grid,
  Tooltip,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../app/hooks";
import isEmpty from "../../../../common/helpers/isEmpty";
import { Nullable } from "../../../../common/types/nullable";
import { WorkTitle } from "../../../orders/orderAPI";
import { fetchWorkTitles } from "../../../workTitles/workTitleSlice";
import { FormWorkTitle } from "./types";

type Props = {
  defaultWorkTitle: FormWorkTitle | null;
  disabled?: boolean;
  showUnknownWorkTitleIdInfo?: boolean;
  unknownWorkTitleName?: string;
  requireDropdownSelectionForEezyWork?: boolean;
};

const isWorkTitle = (value: any): boolean => {
  const isObject = typeof value === "object";
  const hasId = value && value.hasOwnProperty("id");
  const hasFieldOfWorkId = value && value.hasOwnProperty("name");
  return isObject && hasId && hasFieldOfWorkId;
};

const WorkTitleControl = ({
  defaultWorkTitle,
  disabled = false,
  showUnknownWorkTitleIdInfo = false,
  unknownWorkTitleName,
  requireDropdownSelectionForEezyWork = false,
}: Props) => {
  const dispatch = useAppDispatch();
  const { i18n, t } = useTranslation();

  const [availableWorkTitles, setAvailableWorkTitles] = useState<
    FormWorkTitle[]
  >([]);

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();
  const isEezyWork = watch("isEezyWork");
  const hasError = !!errors.workTitle;
  const allowFreeText =
    !requireDropdownSelectionForEezyWork || !isEezyWork || disabled;

  const autocompleteFilter = createFilterOptions<FormWorkTitle>();

  useEffect(() => {
    if (isEmpty(availableWorkTitles)) {
      const language = i18n.language.substring(0, 2);
      dispatch(fetchWorkTitles({ language })).then((action) => {
        const fetchedWorkTitles = action.payload as WorkTitle[];
        const asFormWorkTitles: FormWorkTitle[] = fetchedWorkTitles.map(
          (wt): FormWorkTitle => ({
            id: wt.id,
            name: wt.name,
          }),
        );
        setAvailableWorkTitles(asFormWorkTitles);

        // TODO: Handle failing fetch
        return isFulfilled(action);
      });
    }
  }, [availableWorkTitles, dispatch, i18n.language]);

  const formValueFrom = (
    newValue: Nullable<FormWorkTitle | string>,
  ): FormWorkTitle => {
    if (isWorkTitle(newValue)) {
      const selectedWorkTitle = newValue as FormWorkTitle;
      return {
        id: selectedWorkTitle.id,
        name: selectedWorkTitle.name,
      };
    }
    // This happens when Autocomplete input is written and Enter is pressed. newValue is string.
    return {
      id: null,
      name: newValue as string,
    };
  };

  return (
    <>
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("common:workTitle")}
        </Typography>
      </Grid>
      <Grid item container justifyContent="flex-start" alignItems="left" xs={9}>
        {showUnknownWorkTitleIdInfo ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography>{unknownWorkTitleName || ""}</Typography>
            <Tooltip
              title={t("workHistory:unknownWorkTitleIdTooltip")}
              placement="right"
              arrow
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: "warning.main", cursor: "pointer" }}
              />
            </Tooltip>
          </Box>
        ) : (
          <Controller
            name="workTitle"
            control={control}
            rules={{
              required: { value: true, message: t("common:requiredField") },
              validate: (value) =>
                !requireDropdownSelectionForEezyWork ||
                !isEezyWork ||
                !!value?.id ||
                t("common:requiredField"),
            }}
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                freeSolo={allowFreeText}
                fullWidth
                clearOnBlur
                defaultValue={defaultWorkTitle}
                disabled={disabled}
                options={availableWorkTitles}
                value={value || null}
                onChange={(_, newValue) => {
                  onChange(
                    newValue != null ? formValueFrom(newValue) : null,
                  );
                }}
                filterOptions={(options, params) => {
                  const filtered = autocompleteFilter(options, params);
                  const { inputValue } = params;

                  const isExisting = options.some(
                    (option) => inputValue === option.name,
                  );

                  if (allowFreeText && inputValue !== "" && !isExisting) {
                    filtered.push({
                      id: null,
                      name: inputValue,
                    });
                  }

                  return filtered;
                }}
                isOptionEqualToValue={(option, selected) =>
                  option.id && selected?.id
                    ? option.id === selected.id
                    : option.name === selected?.name
                }
                getOptionLabel={(option): string => {
                  if (typeof option === "string") {
                    return option;
                  } else {
                    return option.name;
                  }
                }}
                renderOption={(props, option) => (
                  <ListItem {...props} key={option.id}>
                    <ListItemText>{option.name}</ListItemText>
                  </ListItem>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    name="workTitle"
                    error={hasError}
                  />
                )}
              />
            )}
          />
        )}
      </Grid>
    </>
  );
};

export default WorkTitleControl;
